/**
 * The warning lines of a check-conflicts response, in one place (#2041, #2042):
 * - `conflictWarningLines` words every warning source in display order: the employee's
 *   availability first, then the working time warnings (day, week, rest).
 * - `ShiftWarningLines` is the working time block of the create and edit dialogs, under the
 *   overlap and availability lines.
 * - `ShiftWarningsDialog` is the one dialog a drag and drop shows before it moves a shift
 *   that carries any line.
 * Warnings never block: the planner saves or moves anyway. While the shift overlaps another
 * there are no working time lines: that save is refused, and its sums would count both shifts.
 */
import type { I18n } from '@lingui/core';
import { useLingui } from '@lingui/react';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatDateDisplay, formatDuration } from '@/lib/format';
import { shifts_ShiftWarningResponse } from '@/api/models/shifts_ShiftWarningResponse';
import type { ConflictResponse, ShiftWarning } from '@/types/shifts';
import { availabilityLine } from './availabilityLines';

/** One working time warning as a sentence with the value and the limit (#2042). */
export function workingTimeLine(w: ShiftWarning, i18n: I18n): string {
  const values = {
    value: formatDuration(w.value_minutes, i18n.locale),
    limit: formatDuration(w.limit_minutes, i18n.locale),
    date: formatDateDisplay(w.date, i18n.locale),
  };
  switch (w.code) {
    case shifts_ShiftWarningResponse.code.DAY_TOO_LONG:
      return i18n._({ id: 'shifts.warning.dayTooLong', message: 'The day becomes {value} long (limit {limit}).', values });
    case shifts_ShiftWarningResponse.code.WEEK_TOO_LONG:
      return i18n._({
        id: 'shifts.warning.weekTooLong',
        message: 'The week of {date} becomes {value} long (limit {limit}).',
        values,
      });
    default:
      return i18n._({
        id: 'shifts.warning.restTooShort',
        message: 'Only {value} rest before the shift on {date} (minimum {limit}).',
        values,
      });
  }
}

/** Every warning line of a check-conflicts response, in display order. */
export function conflictWarningLines(response: ConflictResponse | undefined | null, employeeName: string, i18n: I18n): string[] {
  if (!response) return [];
  const lines: string[] = [];
  if (response.availability) lines.push(availabilityLine(response.availability, employeeName, i18n));
  if (!response.has_conflict) {
    for (const w of response.warnings ?? []) lines.push(workingTimeLine(w, i18n));
  }
  return lines;
}

/** One person's warnings when the create dialog plans several people (#2121). */
export interface NamedShiftWarnings {
  name: string;
  warnings: ShiftWarning[];
}

/**
 * The working time block of the create and edit dialogs (#2042); nothing without warnings.
 * With `named` (several people, #2121) every line starts with the person's first name.
 */
export function ShiftWarningLines({
  warnings,
  named,
}: {
  warnings: ShiftWarning[] | undefined;
  named?: NamedShiftWarnings[];
}) {
  const { i18n } = useLingui();
  if (named) {
    const lines = named.flatMap((person) =>
      person.warnings.map((w) => ({
        key: `${person.name}-${w.code}-${w.date}`,
        text: i18n._({
          id: 'shifts.warning.personLine',
          message: '{name}: {line}',
          values: { name: person.name, line: workingTimeLine(w, i18n) },
        }),
      }))
    );
    if (lines.length === 0) return null;
    return (
      <Alert variant="warning" data-testid="shift-warning-lines">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>{i18n._({ id: 'shifts.warning.workingTimeTitle', message: 'Working time' })}</AlertTitle>
        <AlertDescription>
          <ul className="space-y-0.5">
            {lines.map((line) => (
              <li key={line.key} data-testid="shift-warning-line">
                {line.text}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-muted-foreground">
            {i18n._({ id: 'shifts.warning.notBlocking', message: 'These are warnings only. Nothing is blocked.' })}
          </p>
        </AlertDescription>
      </Alert>
    );
  }
  if (!warnings?.length) return null;
  return (
    <Alert variant="warning" data-testid="shift-warning-lines">
      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>{i18n._({ id: 'shifts.warning.workingTimeTitle', message: 'Working time' })}</AlertTitle>
      <AlertDescription>
        <ul className="space-y-0.5">
          {warnings.map((w) => (
            <li key={`${w.code}-${w.date}`} data-testid="shift-warning-line">
              {workingTimeLine(w, i18n)}
            </li>
          ))}
        </ul>
        <p className="mt-1 text-xs text-muted-foreground">
          {i18n._({ id: 'shifts.warning.notBlocking', message: 'These are warnings only. Nothing is blocked.' })}
        </p>
      </AlertDescription>
    </Alert>
  );
}

interface ShiftWarningsDialogProps {
  open: boolean;
  lines: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

/** "Trotzdem verschieben?": the warnings of a drop, then move or cancel. */
export function ShiftWarningsDialog({ open, lines, onConfirm, onCancel }: ShiftWarningsDialogProps) {
  const { i18n } = useLingui();
  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <AlertDialogContent data-testid="move-warnings-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {i18n._({ id: 'shifts.dnd.warningsTitle', message: 'Move anyway?' })}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <ul className="space-y-1.5">
              {lines.map((line) => (
                <li key={line} className="flex items-start gap-2" data-testid="move-warning-line">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-on-surface" aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{i18n._({ id: 'common.cancel', message: 'Cancel' })}</AlertDialogCancel>
          <AlertDialogAction data-testid="move-warnings-confirm" onClick={onConfirm}>
            {i18n._({ id: 'shifts.dnd.moveAnyway', message: 'Move anyway' })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
